import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  parse
} from 'date-fns';
import { StockPeriods } from 'enum/periods';
import { TransactionType } from 'enum/transaction-type';
import { DataFetchStatusStatus, Segment } from 'types/custom/common';
import {
  AccountCompressed,
  AccountConfigCompressed,
  HoldingCompressed,
  PassbookCompressed,
  PositionCompressed,
  SimulationOrderCompressed,
  StockProfitCompressed,
  TransactionCompressed
} from 'types/custom/entity-compressed';
import { OrderItem } from 'types/custom/order';
import {
  BalanceAmount,
  ConfigEntry,
  PassbookSimulation,
  SimulationData,
  SimulationOrder,
  SimulationStatus,
  Simulations,
  StockProfit,
  TransactionAccount,
  TransactionSimulation,
  WebsocketSimulationAccount
} from 'types/custom/simulation';
import { Holding, Position } from 'types/custom/user';
import { formatDateCommon } from './formatters';

export const getActiveUsersCountFromSelected = (users: any): number => {
  return users?.filter((user: any) => user?.loggedIn).length || 0;
};

export const getOrderStatusColor = (status: string): 'success' | 'warning' | 'info' | 'error' | 'default' => {
  switch (status) {
    case 'completed':
    case 'approved':
      return 'success';
    case 'pending':
    case 'open':
    case 'trigger_pending':
      return 'warning';
    case 'cancelled':
    case 'failed':
    case 'rejected':
      return 'error';
    case 'others':
      return 'info';
    default:
      return 'default';
  }
};

export const calculateOrderPlacedCount = (orderList: OrderItem[]) => {
  if (orderList.length === 0) {
    return { success: 0, failed: 0 };
  }
  const result = orderList.reduce(
    (acc: any, curr: any) => {
      switch (curr.orderStatus) {
        case 'open':
        case 'completed':
        case 'others':
          acc.success++;
          break;
        case 'failed':
        case 'rejected':
          acc.failed++;
          break;
        default:
          acc.failed++;
          break;
      }
      return acc;
    },
    { success: 0, failed: 0 }
  );
  return result;
};

export const calculateLivePnl = (averagePrice: number, tickPrice: number, quantity: number): number => {
  return (tickPrice - averagePrice) * quantity;
};

export const calculateTotalPnlFromHoldings = (data: (Position | Holding)[]): number => {
  if (data.length === 0) {
    return 0;
  }
  return data.reduce((accumulator: number, currentItem: Position | Holding) => accumulator + currentItem.pnl, 0);
};

export function areAllKeysEmpty(obj: any) {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
        return false;
      }
    }
  }
  return true;
}

export function sortByTimestampDesc(a: any, b: any): number {
  const timestampA = new Date(a.timestamp).getTime();
  const timestampB = new Date(b.timestamp).getTime();

  if (timestampA < timestampB) {
    return 1;
  }
  if (timestampA > timestampB) {
    return -1;
  }
  return 0;
}

export const convertSpeedToDelay = (speed: number): number => {
  switch (speed) {
    case 1:
      return 1000;
    case 2:
      return 800;
    case 3:
      return 600;
    case 4:
      return 200;
    case 5:
      return 100;
    case 6:
      return 50;
    case 7:
      return 10;
    default:
      return 200;
  }
};

export const convertDelayToSpeed = (delay: number): number => {
  switch (true) {
    case delay >= 1000:
      return 1;
    case delay >= 800:
      return 2;
    case delay >= 600:
      return 3;
    case delay >= 200:
      return 4;
    case delay >= 100:
      return 5;
    case delay >= 50:
      return 6;
    case delay >= 10:
      return 7;
    default:
      return 0;
  }
};

export const getDisplayValueFromSimulationStatus = (status: SimulationStatus | DataFetchStatusStatus) => {
  switch (status) {
    case SimulationStatus.pending:
      return 'PENDING';
    case SimulationStatus.inProgress:
      return 'IN PROGRESS';
    case SimulationStatus.completed:
      return 'COMPLETED';
    case SimulationStatus.preparing:
      return 'PREPARING';
    case SimulationStatus.error:
      return 'ERROR';
    case SimulationStatus.stopped:
      return 'STOPPED';
  }
};

export const getSimulationStatusColorFromStatus = (status: SimulationStatus | DataFetchStatusStatus) => {
  switch (status) {
    case SimulationStatus.pending:
    case SimulationStatus.preparing:
      return 'warning';
    case SimulationStatus.inProgress:
      return 'primary';
    case SimulationStatus.completed:
      return 'success';
    case SimulationStatus.error:
    case SimulationStatus.stopped:
      return 'error';
  }
};

export function findProfitableAndNonProfitableTradesFromTotal(simulations: Simulations[]): {
  totalTrades: number;
  profitable: number;
  nonProfitable: number;
  buys: number;
  sells: number;
} {
  if (simulations.length === 0) {
    return { totalTrades: 0, profitable: 0, nonProfitable: 0, buys: 0, sells: 0 };
  }
  const result = simulations.reduce(
    (acc, curr) => {
      curr.transactions.forEach((t) => {
        if (t.netProfitOrLoss !== 0) {
          t.netProfitOrLoss > 0 ? acc.profitable++ : acc.nonProfitable++;
        }

        if (t.action === TransactionType.SELL) {
          acc.sells++;
          acc.totalTrades++;
        } else {
          acc.buys++;
        }
      });
      return acc;
    },
    { totalTrades: 0, profitable: 0, nonProfitable: 0, buys: 0, sells: 0 }
  );

  return result;
}

export const calculateProfits = (
  simulation: SimulationData | null,
  period: string
): { totalProfitPercentage: number; percentageInGivenPeriod: number } => {
  const result = { totalProfitPercentage: 0, percentageInGivenPeriod: 0 };
  if (!simulation) {
    return result;
  }

  const totalProfitPercentage = (simulation?.netProfitOrLoss / simulation?.account?.capitalAmount) * 100;

  const startDate = new Date(simulation?.startTime);
  const endDate = new Date(simulation?.endTime);
  const months = calculatePeriodDifference('monthly', startDate, endDate);

  const percentageInGivenPeriod = (totalProfitPercentage / months) * 12;

  return { totalProfitPercentage, percentageInGivenPeriod };
};

const calculatePeriodDifference = (period: string, startDate: Date, endDate: Date): number => {
  switch (period) {
    case 'daily':
      return differenceInCalendarDays(endDate, startDate);
    case 'weekly':
      return Math.ceil(differenceInCalendarDays(endDate, startDate) / 7);
    case 'monthly':
      return differenceInCalendarMonths(endDate, startDate);
    case 'yearly':
      return differenceInCalendarYears(endDate, startDate);
    default:
      throw new Error('Invalid period specified.');
  }
};

export const calculateTotalTimeTaken = (startTime: string, endTime: string): string => {
  const startDate = parse(startTime, 'dd-MMM-yyyy hh:mm:ss a', new Date());
  const endDate = parse(endTime, 'dd-MMM-yyyy hh:mm:ss a', new Date());

  const timeDifference = endDate.getTime() - startDate.getTime();

  const hours = Math.floor(timeDifference / (1000 * 60 * 60));
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

  let totalTimeTaken = '';

  if (hours > 0) {
    totalTimeTaken += `${hours} hour${hours > 1 ? 's' : ''}, `;
  }
  if (minutes > 0 || hours > 0) {
    totalTimeTaken += `${minutes} minute${minutes > 1 ? 's' : ''}, `;
  }
  totalTimeTaken += `${seconds} second${seconds > 1 ? 's' : ''}`;

  return totalTimeTaken;
};

export const calculateStats = (
  simulation: SimulationData | null
): {
  mostProfitable: TransactionSimulation | null;
  leastProfitable: TransactionSimulation | null;
  highestBalance: BalanceAmount | null;
  lowestBalance: BalanceAmount | null;
  mostProfitableStock: string | null;
  leastProfitableStock: string | null;
} | null => {
  if (!simulation || !simulation?.simulations || simulation?.simulations?.length === 0) {
    return null;
  }

  let mostProfitable: TransactionSimulation | null = null;
  let leastProfitable: TransactionSimulation | null = null;
  let highestBalance: BalanceAmount | null = null;
  let lowestBalance: BalanceAmount | null = null;
  let mostProfitableStock: string | null = null;
  let leastProfitableStock: string | null = null;

  simulation?.simulations?.forEach((s) => {
    s.transactions.forEach((t) => {
      if (!mostProfitable || t?.netProfitOrLoss > mostProfitable?.netProfitOrLoss) {
        mostProfitable = t;
        mostProfitableStock = s?.stockId;
      }
      if (!leastProfitable || t?.netProfitOrLoss < leastProfitable?.netProfitOrLoss) {
        leastProfitable = t;
        leastProfitableStock = s?.stockId;
      }
    });
  });

  simulation?.account?.passbook?.forEach((p) => {
    if (!highestBalance || p?.balance > highestBalance?.amount) {
      highestBalance = { amount: p?.balance, date: p?.timestamp! };
    }
    if (!lowestBalance || p?.balance < lowestBalance.amount) {
      lowestBalance = { amount: p?.balance, date: p?.timestamp! };
    }
  });
  return { mostProfitable, leastProfitable, highestBalance, lowestBalance, leastProfitableStock, mostProfitableStock };
};

export function getRandomColor() {
  const colors = ['primary', 'secondary', 'info', 'success', 'warning', 'error', 'default'];
  const randomIndex = Math.floor(Math.random() * colors.length);
  return colors[randomIndex];
}

export const splitAndCapitalize = (str: string): string => {
  return str
    .split('_')
    .map((word) => word.charAt(0)?.toUpperCase() + word.slice(1))
    .join(' ');
};

export const calculateRunningTime = (createdOn: string) => {
  const now = new Date();
  const createdDate = new Date(createdOn);
  const diffInDays = differenceInDays(now, createdDate);
  const diffInHours = differenceInHours(now, createdDate) % 24;
  const diffInMinutes = differenceInMinutes(now, createdDate) % 60;

  let result = '';
  if (diffInDays > 0) {
    result += ` ${diffInDays} days `;
  }
  if (diffInHours > 0) {
    result += ` ${diffInHours} hours`;
  }
  result += ` ${diffInMinutes} minutes`;

  return result.trim();
};

export function findAtTheMoney(ltp: number, strikePrices: number[]): number {
  let result = 0;
  for (let i = 0; i < strikePrices.length; i++) {
    if (ltp >= strikePrices[i] && ltp <= strikePrices[i + 1]) {
      result = strikePrices[i];
    }
  }
  return result;
}

export function calculateCAGR(
  initialCapital: number,
  yearlyProfitPercentage: number,
  timePeriod: number,
  periodType: 'years' | 'months'
): { cagrPercentage: number; cagrAmount: number } {
  // Convert time period to years if given in months
  const years = periodType === 'months' ? timePeriod / 12 : timePeriod;

  const yearlyGrowthFactor = 1 + yearlyProfitPercentage / 100;

  // Final Value after the given number of years
  const finalValue = initialCapital * Math.pow(yearlyGrowthFactor, years);

  // CAGR Amount
  const cagrAmount = finalValue - initialCapital;

  // CAGR Percentage
  const cagrPercentage = ((finalValue / initialCapital) ** (1 / years) - 1) * 100;

  return {
    cagrPercentage: parseFloat(cagrPercentage.toFixed(2)), // Round to 2 decimal places
    cagrAmount: parseFloat(cagrAmount.toFixed(2)) // Round to 2 decimal places
  };
}

export function calculateCAGRPercentage(initialValue: number, finalValue: number, years: number) {
  if (initialValue <= 0 || finalValue <= 0 || years <= 0) {
    return 0;
    //throw new Error('Values must be greater than 0');
  }

  const cagr = (Math.pow(finalValue / initialValue, 1 / years) - 1) * 100;
  return cagr;
}

export function calculatePerOrderPnlMap(orders: SimulationOrder[]): Record<string, any> {
  const pnlMap: Record<string, any> = {};

  // Group orders by unique instrument (so CE and PE aren't mixed up)
  const ordersByStock: Record<string, SimulationOrder[]> = {};
  orders.forEach((o) => {
    const uniqueId = getStockId(o.stockId, o.optionType, o.option, o.optionExpiryDate, o.strikePrice, o.segment, o.futureExpiryDate);
    if (uniqueId) {
      if (!ordersByStock[uniqueId]) ordersByStock[uniqueId] = [];
      ordersByStock[uniqueId].push(o);
    }
  });

  Object.values(ordersByStock).forEach((stockOrders) => {
    const buyLots: { quantity: number; price: number }[] = [];
    const sellLots: { quantity: number; price: number }[] = [];

    const completedOrders = stockOrders.filter(
      (order) =>
        ['completed', 'approved'].includes((order.status || '').toLowerCase()) &&
        (order.price > 0 || order.originalPrice > 0) &&
        order.quantity != null
    );

    // Sort chronologically by execution time, using orderId as a tie-breaker for same-second executions
    const sorted = [...completedOrders].sort((a, b) => {
      const timeDiff = new Date(a.executionTimestamp ?? a.timestamp).getTime() - new Date(b.executionTimestamp ?? b.timestamp).getTime();
      return timeDiff !== 0 ? timeDiff : (a.orderId || 0) - (b.orderId || 0);
    });

    // Group into "rounds" — orders that share the same basketId or orderGroupId
    // are part of the same basket event and must NOT be matched against each other.
    // Orders without a basket identifier get their own individual round.
    const rounds: SimulationOrder[][] = [];
    const processedOrderIds = new Set<number>();

    sorted.forEach((order) => {
      if (processedOrderIds.has(order.orderId)) return;

      const basketKey = order.basketId || (order.orderGroupId ? String(order.orderGroupId) : null);
      if (basketKey) {
        // Collect all orders in the same basket
        const basketOrders = sorted.filter(
          (o) =>
            (o.basketId === basketKey || (o.orderGroupId && String(o.orderGroupId) === basketKey)) &&
            !processedOrderIds.has(o.orderId)
        );
        basketOrders.forEach((o) => processedOrderIds.add(o.orderId));
        rounds.push(basketOrders);
      } else {
        processedOrderIds.add(order.orderId);
        rounds.push([order]);
      }
    });

    // Process each round: match orders in this round against the open lots
    // from PREVIOUS rounds only (orders within the same round are co-entry / co-exit)
    rounds.forEach((round) => {
      const roundResults: { order: SimulationOrder; realizedPnl: number }[] = [];

      round.forEach((order) => {
        const qty = Math.abs(order.quantity);
        const price = order.price && order.price > 0 ? order.price : order.originalPrice;
        let remainingQuantity = qty;
        let realizedPnl = 0;

        if (order.action === 'BUY') {
          while (remainingQuantity > 0 && sellLots.length > 0) {
            const sellLot = sellLots[0]; // Back to FIFO to ensure sequential matching
            const matchedQuantity = Math.min(remainingQuantity, sellLot.quantity);
            realizedPnl += (sellLot.price - price) * matchedQuantity;
            remainingQuantity -= matchedQuantity;
            sellLot.quantity -= matchedQuantity;
            if (sellLot.quantity === 0) sellLots.shift();
          }
          if (remainingQuantity > 0) buyLots.push({ quantity: remainingQuantity, price });
        }

        if (order.action === 'SELL') {
          while (remainingQuantity > 0 && buyLots.length > 0) {
            const buyLot = buyLots[0]; // Back to FIFO to ensure sequential matching
            const matchedQuantity = Math.min(remainingQuantity, buyLot.quantity);
            realizedPnl += (price - buyLot.price) * matchedQuantity;
            remainingQuantity -= matchedQuantity;
            buyLot.quantity -= matchedQuantity;
            if (buyLot.quantity === 0) buyLots.shift();
          }
          if (remainingQuantity > 0) sellLots.push({ quantity: remainingQuantity, price });
        }

        roundResults.push({ order, realizedPnl });
      });

      // Write P&L results for all orders in this round
      roundResults.forEach(({ order, realizedPnl }) => {
        const qty = Math.abs(order.quantity);
        const price = order.price && order.price > 0 ? order.price : order.originalPrice;
        const brokerage = order.brokerage || 0;
        const interest = order.interest || 0;
        const netPnl = realizedPnl - brokerage - interest;
        const pct = realizedPnl !== 0 && price > 0 ? (netPnl / (price * qty)) * 100 : null;

        pnlMap[order.orderId] = {
          profitOrLoss: realizedPnl,
          brokerage,
          interest,
          netProfitOrLoss: netPnl,
          netProfitOrLossPercentage: pct
        };
      });
    });
  });

  return pnlMap;
}

export function calculateFutureValue(currentValue: number, cagrPercentage: number, years: number) {
  const cagrDecimal = cagrPercentage / 100;

  const futureValue = currentValue * Math.pow(1 + cagrDecimal, years);
  return futureValue;
}

export function capitalizeFirstLetter(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function calculateGrowthPercentage(investmentAmount: number, currentValue: number): number {
  const growthPercentage = (currentValue / investmentAmount) * 100;
  return parseFloat(growthPercentage.toFixed(2));
}

export function camelCaseToNormal(text: string): string {
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space between lowercase and uppercase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle consecutive uppercase letters
    .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter
}

export function getTimeFromStockPeriod(stockPeriod: StockPeriods): number {
  switch (stockPeriod) {
    case StockPeriods.ONE_MINUTE:
      return 1;
    case StockPeriods.TWO_MINUTES:
      return 2;
    case StockPeriods.FIVE_MINUTES:
      return 5;
    case StockPeriods.TEN_MINUTES:
      return 10;
    case StockPeriods.FIFTEEN_MINUTES:
      return 15;
    case StockPeriods.HALF_HOUR:
      return 30;
    case StockPeriods.ONE_HOUR:
      return 60;
    case StockPeriods.WEEKLY:
      return 7 * 24 * 60;
    case StockPeriods.YEAR:
      return 365 * 24 * 60;
    default:
      return 0;
  }
}

export function getStockPeriodNumberList(stockPeriods: StockPeriods[]) {
  return stockPeriods.map((p) => getTimeFromStockPeriod(p));
}

export const isMultipleOfAnyInterval = (timestamp: Date, intervals: number[]) => {
  const minutes = timestamp.getMinutes();
  return intervals.some((interval) => minutes % interval === 0);
};

function compressPassbook(passbook: PassbookSimulation): PassbookCompressed {
  return {
    t: passbook?.timestamp ?? '',
    d: passbook?.description ?? '',
    d2: passbook?.debit ?? 0,
    c: passbook?.credit ?? 0,
    b: passbook?.balance ?? 0
  };
}

function compressConfig(config: ConfigEntry): AccountConfigCompressed {
  return {
    i: config?.initialCapital ?? 0,
    i2: config?.initialRisk ?? 0,
    s: config?.strategy ?? '',
    c: config?.capitalAmount ?? 0,
    r: config?.risk ?? 0,
    w: config?.withdrawalProfitPercentage ?? 0,
    d: config?.debt ?? 0
  };
}

function compressPosition(position: Position): PositionCompressed {
  return {
    s: position?.stockId ?? '',
    p: position?.product ?? '',
    q: position?.quantity ?? 0,
    p2: position?.price ?? 0,
    p3: position?.pnl ?? 0,
    l: position?.lastTradedPrice ?? 0,
    i: position?.intraday ?? false,
    s3: position?.strikePrice ?? 0,
    p4: position?.positionOrders ?? [],
    e: position?.expectedQuantity ?? 0,
    o3: position?.orderType ?? '',
    o: position?.optionType ?? '',
    o2: position?.optionExpiryDate ? new Date(position.optionExpiryDate).getTime() : 0,
    s2: position?.stockName ?? '',
    o4: position.option ?? false
  };
}

function compressOrder(order: SimulationOrder, userId: string): SimulationOrderCompressed {
  return {
    r: order?.requestId ?? 0,
    o: order?.orderId ?? '',
    s: order?.simulationId ?? '',
    s2: order?.stockId ?? '',
    s3: order?.stockName ?? '',
    a: order?.action ?? '',
    p: order?.price ?? 0,
    t: order?.triggerPrice ?? 0,
    q: order?.quantity ?? 0,
    s4: order?.status ?? '',
    t2: order?.timestamp ?? '',
    e: order?.executionTimestamp ?? '',
    r2: order?.reason ?? '',
    o5: order?.orderGroupId ?? '',
    o6: order?.originalPrice ?? 0,
    o7: order?.optionType ?? '', // Option type (e.g., "CE")
    s6: order?.strikePrice ?? 0,
    u: order?.underlyingStockId ?? '',
    u2: userId ?? '',
    i: order?.intraday ?? false,
    l: order?.liveOrderId ?? '',
    s7: order?.strategy ?? '',
    s8: order?.strategyDisplayName ?? '',
    m: order?.manualOrder ?? false,
    b: order?.basketId ?? '',
    o2: order?.optionExpiryDate ?? '',
    o8: order.option ?? false
  };
}

function compressTransaction(transaction: TransactionAccount): TransactionCompressed {
  return {
    t: transaction?.transactonId ?? '',
    d: transaction?.description ?? '',
    o: transaction?.optionExpiryDate ?? '',
    t2: transaction?.timestamp ?? '',
    a: transaction?.action ?? '',
    p: transaction?.price ?? 0,
    s: transaction?.strikePrice ?? 0,
    o2: transaction?.optionType ?? '',
    r: transaction?.reason ?? '',
    o3: transaction?.orderQuantity ?? 0,
    p2: transaction?.profitOrLoss ?? 0,
    b: transaction?.brokerage ?? 0,
    n: transaction?.netProfitOrLoss ?? 0,
    n2: transaction?.netProfitOrLossPercentage ?? 0,
    o4: transaction?.orderType ?? '',
    l: transaction?.longDescription ?? '',
    s2: transaction?.stockId ?? '',
    s3: transaction?.stockName ?? '',
    l2: transaction?.leverage ?? 0
  };
}

function compressHolding(holding: Holding): HoldingCompressed {
  return {
    s: holding?.stockId!,
    e: holding?.exchange,
    p: holding?.product,
    h: holding?.holdingQuantity,
    t: holding?.t1Quantity,
    p2: holding?.price,
    l: holding?.lastTradedPrice,
    l2: holding?.lastDayClose,
    d: holding?.dayPnL,
    p3: holding?.pnl,
    p4: holding?.positionType
  };
}

function compressStockProfit(stockProfit: StockProfit): StockProfitCompressed {
  return {
    s: stockProfit?.stockId ?? '',
    t: stockProfit?.totalProfitOrLoss ?? 0,
    t2: stockProfit?.totalBrokerage ?? 0,
    n: stockProfit?.netProfitOrLoss ?? 0,
    h: stockProfit?.holdingPnl ?? 0
  };
}

export function compressAccounts(accounts: WebsocketSimulationAccount[]): AccountCompressed[] {
  return accounts.map((account) => {
    return {
      a: account.accountId ?? '', // Account ID
      a2: account.accountName ?? '', // Account Name (fallback to empty if null)
      f: account.firstName ?? '', // First Name
      l: account.lastName ?? '', // Last Name
      b: account.balance ?? 0, // Balance
      p: account?.passbook?.map(compressPassbook) ?? [], // Assuming you have a function compressPassbook
      c: account?.config?.map(compressConfig) ?? [], // Assuming you have a function compressConfig
      p2: account.profitWithdrawals, // Profit Withdrawals (any[])
      u: account.userId ?? '', // User ID (fallback to empty if null)
      p3: account?.positions?.map(compressPosition) ?? [], // Positions
      h: account?.holdings?.map(compressHolding) ?? [], // Holdings
      o: account?.orderList?.map((o) => compressOrder(o, account?.userId!)) ?? [], // Orders
      t: account?.transactionList?.map(compressTransaction) ?? [], // Transactions
      s: account?.stockProfits?.map(compressStockProfit) ?? [], // Stock Profits
      t2: account.totalProfitLoss ?? account?.totalProfitOrLoss ?? 0, // Total Profit/Loss
      d: account?.dayProfitLoss ?? account?.dayPnL ?? 0, // Day Profit/Loss
      t3: account.totalBrokerage ?? 0, // Total Brokerage
      c2: account.capitalAmount ?? 0, // Capital Amount
      e: account.enabled ?? false, // Profit Withdrawal Enabled
      l2: account.loggedIn ?? false
    };
  });
}

/**
 * Calculates Profit or Loss for a trade position.
 * @param ltp - Last traded price (current market price)
 * @param entryPrice - Entry price of the trade
 * @param quantity - Quantity of the position
 * @param side - "BUY" or "SELL"
 * @returns number (P&L value)
 */
export function calculatePnL(ltp: number, entryPrice: number, quantity: number): number {
  if (!ltp || !entryPrice || !quantity) return 0;
  const side = quantity > 0 ? 'BUY' : 'SELL';

  // Equation:
  // PnL = (LTP - EntryPrice) * Quantity   --> for BUY
  // PnL = (EntryPrice - LTP) * Quantity   --> for SELL
  return side === 'BUY' ? (ltp - entryPrice) * quantity : (entryPrice - ltp) * Math.abs(quantity);
}
export function getStockId(
  stockId: string,
  optionType: string,
  option: boolean,
  optionExpiryDate: string,
  strikePrice: number,
  segment: Segment,
  futureExpiryDate: string
) {
  const seg = String(segment).toUpperCase();
  const isFuture = seg === 'FUTURE' || seg === 'FUTURES';

  const formattedDate = formatDateCommon(isFuture ? futureExpiryDate : optionExpiryDate ?? '0', 'ddMMMyy');

  const stock = isFuture
    ? `${stockId}_${formattedDate.toUpperCase()}`
    : option
    ? [stockId, strikePrice, optionType, formattedDate.toUpperCase()].filter(Boolean).join('_')
    : stockId;

  return stock;
}
